<template>
  <div
    ref="cardRef"
    class="invitee-card position-relative d-flex align-center rounded border border-sm border-grey-lighten-2"
    :class="cardClass"
    :data-test-id="`participant-${participant.signeeId}`"
    :draggable="isDraggable"
    @dragend="onDragEnd"
    @dragstart="onDragStart"
  >
    <!-- APPROVER: a single full-width row (no signature placement) -->
    <template v-if="isApprover">
      <v-icon aria-hidden="true" class="text-grey-darken-1 flex-0-0" size="20">
        fal fa-user-check
      </v-icon>
      <span
        class="invitee-card__email text-body-2 text-truncate flex-grow-1 text-left"
        :class="emailClass"
      >
        {{ participant.email || i18n.t('texts.no_email_yet') }}
      </span>
      <v-chip class="flex-0-0" color="primary" size="small" variant="tonal">
        {{ i18n.t('texts.approver') }}
      </v-chip>
    </template>

    <!-- SIGNEE: signature card with default / placed / drag-over / hover states -->
    <template v-else>
      <!-- Attachment affordance: only the first signee in order can attach -->
      <v-icon
        v-if="attachmentsAllowed"
        aria-hidden="true"
        class="invitee-card__attachment position-absolute top-0 right-0 ma-2 text-grey-darken-1"
        data-test-id="participant-attachment"
        size="16"
      >
        {{ ATTACHMENT_ICON }}
      </v-icon>

      <!-- Top zone: resting sign icon. Desktop swaps it for Place/View on hover;
           compact keeps the icon and puts Place/View under the email. -->
      <div
        class="invitee-card__top position-relative d-flex align-center justify-center"
      >
        <SignIcon
          aria-hidden="true"
          class="invitee-card__sign-icon"
          :class="signIconClass"
        />
        <!-- Desktop hover Place/View. The position menu only opens with 2+
             placements; a single placement is a direct jump in onDesktopAction.
             Bound to @click (not @mousedown): a click never fires after a real
             drag has started, so this naturally distinguishes "user clicked
             the action" from "user is dragging the card" without extra state. -->
        <SignaturePositionMenu
          v-if="!isCompact"
          v-model="menuOpen"
          :disabled="!hasMultiplePlacements"
          :participant="participant"
        >
          <template #activator="{ props: menuProps }">
            <v-tooltip location="bottom" :text="actionTooltip">
              <template #activator="{ props: tooltipProps }">
                <span
                  v-bind="mergeProps(menuProps, tooltipProps)"
                  class="invitee-card__action deep-text-button position-absolute d-inline-flex align-center justify-center ga-1 cursor-pointer"
                  data-test-id="participant-placement-action"
                  @click="onDesktopAction"
                >
                  <span
                    class="invitee-card__action-icon position-relative d-inline-flex"
                  >
                    <v-icon aria-hidden="true" size="16">
                      {{ actionIcon }}
                    </v-icon>
                    <span
                      v-if="hasMultiplePlacements"
                      class="invitee-card__count"
                      data-test-id="participant-placement-count"
                    >
                      {{ placedCount }}
                    </span>
                  </span>
                  {{ actionLabel }}
                </span>
              </template>
            </v-tooltip>
          </template>
        </SignaturePositionMenu>
      </div>

      <!-- Email (or the quorum group's slot label, when rendered as a shared
           signature slot instead of a specific signee's email) -->
      <span
        class="invitee-card__email invitee-card__email--clamp text-body-2 pr-2"
        :class="emailClass"
      >
        {{ slotLabel || participant.email || i18n.t('texts.no_email_yet') }}
      </span>

      <!-- Compact (mdAndDown): Place stays visible so users can keep adding
           without drag-and-drop; View appears once something is placed.
           Both sit on one row as Place | View. -->
      <div
        v-if="isCompact"
        class="invitee-card__compact-actions d-flex align-center justify-center flex-nowrap ga-1 w-100 px-1"
      >
        <button
          v-if="!isLockedQuorumSlot"
          class="d-inline-flex align-center flex-shrink-1 ga-1 pa-0 bg-transparent text-primary deep-caption text-no-wrap cursor-pointer"
          data-test-id="participant-place-action"
          type="button"
          @click="onCompactPlace"
        >
          <v-icon aria-hidden="true" size="12">
            {{ PLACE_ICON }}
          </v-icon>
          {{ i18n.t('buttons.place') }}
        </button>

        <span
          v-if="!isLockedQuorumSlot && isPlaced"
          aria-hidden="true"
          class="text-primary deep-caption user-select-none flex-shrink-0"
        >
          |
        </span>

        <SignaturePositionMenu
          v-if="isPlaced"
          v-model="menuOpen"
          :disabled="!hasMultiplePlacements"
          :participant="participant"
          @navigated="closeDrawerIfCompact"
        >
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="d-inline-flex align-center flex-shrink-1 ga-1 pa-0 bg-transparent text-primary deep-caption text-no-wrap cursor-pointer"
              data-test-id="participant-view-action"
              type="button"
              @click="onCompactView($event)"
            >
              <span
                class="invitee-card__action-icon position-relative d-inline-flex flex-shrink-0"
                :class="{ 'pe-2': hasMultiplePlacements }"
              >
                <v-icon aria-hidden="true" size="12">
                  {{ VIEW_ICON }}
                </v-icon>
                <span
                  v-if="hasMultiplePlacements"
                  class="invitee-card__count"
                  data-test-id="participant-placement-count"
                >
                  {{ placedCount }}
                </span>
              </span>
              {{ i18n.t('buttons.view') }}
            </button>
          </template>
        </SignaturePositionMenu>
      </div>

      <!-- Drag ghost: off-screen blue card used as the custom drag image. -->
      <div
        ref="previewRef"
        aria-hidden="true"
        class="invitee-card__preview position-fixed d-flex flex-column align-center justify-center ga-3 px-3 text-center pointer-events-none rounded elevation-8"
      >
        <SignIcon class="invitee-card__sign-icon text-primary" />
        <span
          class="invitee-card__email text-body-2 text-grey-darken-2 text-truncate"
        >
          {{ slotLabel || participant.email || i18n.t('texts.no_email_yet') }}
        </span>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, mergeProps, nextTick, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useI18n } from 'vue-i18n';
import SignIcon from '@/components/icons/SignIcon.vue';
import SignaturePositionMenu from '@/components/create/SignaturePositionMenu.vue';
import { SigneeType } from '@/types/enums/SigneeType';
import type { PropType } from 'vue';
import type { Signee, SigneeSigningPolicy } from '@/types/Signee';
import type { Placeholder } from '@/composables/placement/placementTypes.ts';
import { PlaceholderType } from '@/types/enums/PlaceholderType.ts';
import type { AxiosResponse } from 'axios';
import {
  injectStrict,
  PlaceHolderComposableKey
} from '@/composables/placement/placementSymbols.ts';
import { useCreateStore } from '@/stores/create.ts';
import { useGlobalStore } from '@/stores/global.ts';
import {
  buildSigneeUpdatePayload,
  scrollToPlaceholder,
  waitForDraggableToSettle
} from '@/composables/placement/usePlacementUtility.ts';
import { useSigneeFilePlacements } from '@/components/create/composables/useSigneeFilePlacements.ts';
import { isPlaceholderForSignee } from '@/utils/signeeSignings.ts';

// Centralised so the icon set is easy to swap when the design is finalised.
const PLACE_ICON = 'fal fa-arrow-down-to-bracket';
const VIEW_ICON = 'fal fa-eye';
const ATTACHMENT_ICON = 'fal fa-paperclip';

const {
  createOrReuseSignatureSlot,
  placePlaceholdersAutomatically,
  currentPlaceholders,
  placeholderTemplateRefs,
  requestPlaceholderRemount,
  isEnvelopeMode
} = injectStrict(PlaceHolderComposableKey);
const createStore = useCreateStore();
const globalStore = useGlobalStore();
const { mdAndDown } = useDisplay();
const isCompact = computed(() => mdAndDown.value);

const props = defineProps({
  participant: {
    type: Object as PropType<Signee>,
    required: true
  },
  // Shows the paperclip affordance. The parent only enables it for the first
  // signee in order, since only they may attach documents.
  attachmentsAllowed: {
    type: Boolean,
    default: false
  },
  // Drop-target highlight while a signature is dragged over the card. The drag
  // event itself is handled elsewhere; this only renders the visual state.
  dragOver: {
    type: Boolean,
    default: false
  },
  // Overrides the displayed email with a slot label (e.g. "Slot 1"). Used by
  // quorum groups, where the card represents one of the group's shared
  // signature slots rather than a specific signee's email — drag & drop,
  // placement and the place/view action stay fully functional, only the
  // label differs.
  slotLabel: {
    type: String as PropType<string | null>,
    default: null
  }
});

const i18n = useI18n();

const isApprover = computed(
  () => props.participant.signeeType === SigneeType.APPROVE
);
const isSigneeCard = computed(() => !isApprover.value);

// Placement state is scoped to the file currently open in the viewer: a signee
// placed on other files but not this one reads as unplaced here. The position
// navigation itself lives in SignaturePositionMenu.
const { currentFileId, placedCount, navigablePlaceholders } =
  useSigneeFilePlacements(() => props.participant);

const isPlaced = computed(() => placedCount.value > 0);
const hasMultiplePlacements = computed(() => placedCount.value > 1);

// In envelope mode, the effective "can modify autograph position" permission
// is decided per file/signing (`signingPolicy`) and can override the signee's
// own `policy`. Look up the placeholder that represents this signee on the
// file currently open in the viewer (built even before the signee has placed
// anything, see `usePlacement.ts`) and read its `signingPolicy` from there;
// outside envelope mode (or if no matching placeholder exists yet) fall back
// to the signee's own policy, as before.
const currentFilePlaceholder = computed<Placeholder | undefined>(() =>
  (currentPlaceholders.value as Placeholder[]).find(
    (placeholder) =>
      isPlaceholderForSignee(
        placeholder,
        props.participant.signeeId,
        createStore.document?.signeeGroups
      ) &&
      placeholder.fileId === currentFileId.value &&
      placeholder.placeHolderType === PlaceholderType.SIGNATURE
  )
);

const canModifyAutographPosition = computed(() => {
  if (isEnvelopeMode.value) {
    const signingPolicy = currentFilePlaceholder.value?.signingPolicy as
      SigneeSigningPolicy | undefined;
    if (signingPolicy) {
      return Boolean(signingPolicy.canModifyAutographPosition);
    }
  }
  return Boolean(props.participant?.policy?.canModifyAutographPosition);
});

// A quorum group's shared slot (rendered via `slotLabel`, see the prop docs)
// can only ever hold one position — once placed on the current file it isn't
// "this signee's" own extra slot to add another one to, so drag & drop and
// the click-to-place action are disabled. Only the View/navigation affordance
// (enabled by `isPlaced`) still works so the user can jump to what's already
// there.
const isLockedQuorumSlot = computed(
  () => Boolean(props.slotLabel) && isPlaced.value
);

const actionLabel = computed(() =>
  isPlaced.value ? i18n.t('buttons.view') : i18n.t('buttons.place')
);
const actionIcon = computed(() => (isPlaced.value ? VIEW_ICON : PLACE_ICON));
const actionTooltip = computed(() =>
  isPlaced.value
    ? i18n.t('labels.view_placed_signatures_on_document')
    : i18n.t('labels.place_signature_on_document')
);

const signIconClass = computed(() =>
  isPlaced.value && !props.dragOver ? 'text-grey' : 'text-primary'
);

const emailClass = computed(() => {
  if (!props.slotLabel && !props.participant.email) {
    return 'text-grey font-italic';
  }
  if (isPlaced.value && !props.dragOver) return 'text-grey';
  return 'text-grey-darken-2';
});

// Open state of the position-navigation menu. Kept here (not just in the menu)
// so the card can keep the View action revealed while the menu is open.
const menuOpen = ref(false);

// True while a Place action's slot creation + persist request is in flight.
// Used purely as a re-entry guard so a double/fast click can't create a second
// slot and fire a duplicate updateInvitee call before `isPlaced` flips the
// action from "Place" to "View". Intentionally not surfaced visually.
const isPlacing = ref(false);

// --- Drag source -----------------------------------------------------------
// The card is the drag handle. The source turns into a dashed "blueprint" while
// a blue ghost (previewRef) follows the cursor. Where the signature lands on the
// document is handled elsewhere; here we only drive the drag-source visuals.
const cardRef = ref<HTMLElement | null>(null);
const previewRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

// Layout/type/cursor via Vuetify utilities; modifiers keep hover/drag visual
// states that utilities can't express (:hover, child opacity, aspect-ratio).
const cardClass = computed(() => ({
  'invitee-card--approver': isApprover.value,
  'invitee-card--placed':
    isSigneeCard.value && isPlaced.value && !props.dragOver,
  'invitee-card--drag-over': isSigneeCard.value && props.dragOver,
  'invitee-card--dragging': isSigneeCard.value && isDragging.value,
  'invitee-card--menu-open': isSigneeCard.value && menuOpen.value,
  'invitee-card--locked': isSigneeCard.value && isLockedQuorumSlot.value,
  'invitee-card--compact': isSigneeCard.value && isCompact.value,
  'flex-column justify-center text-center border-dashed border-md':
    isSigneeCard.value,
  'flex-row justify-start text-left pa-3 px-4 ga-3': isApprover.value,
  'ga-3 px-3': isSigneeCard.value && !isCompact.value,
  'ga-4 pa-3': isSigneeCard.value && isCompact.value,
  'cursor-move': isDraggable.value,
  'cursor-default':
    isApprover.value ||
    isLockedQuorumSlot.value ||
    (isSigneeCard.value && isCompact.value)
}));

const isDraggable = computed(
  () =>
    (isSigneeCard.value &&
      !isLockedQuorumSlot.value &&
      !isCompact.value &&
      canModifyAutographPosition.value) ||
    (isSigneeCard.value && !isLockedQuorumSlot.value && !isCompact.value)
);

function onDragStart(event: DragEvent) {
  if (!isSigneeCard.value || isLockedQuorumSlot.value || isCompact.value) {
    event.preventDefault();
    return;
  }
  const card = cardRef.value;
  const preview = previewRef.value;
  if (event.dataTransfer && card && preview) {
    // Firefox needs data set for the drag to begin.
    event.dataTransfer.setData('text/plain', props.participant.signeeId);
    event.dataTransfer.setData(
      'application/x-signee-id',
      props.participant.signeeId
    );
    event.dataTransfer.setData(
      'application/x-email',
      props.participant.email || ''
    );

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copy';
    }

    // Match the ghost width to the card and grab it under the cursor.
    preview.style.width = `${card.offsetWidth}px`;
    const rect = card.getBoundingClientRect();
    const cursorOffsetXRatio = Math.min(
      Math.max((event.clientX - rect.left) / rect.width, 0),
      1
    );
    const cursorOffsetYRatio = Math.min(
      Math.max((event.clientY - rect.top) / rect.height, 0),
      1
    );
    event.dataTransfer.setData(
      'application/x-cursor-offset-x-ratio',
      String(cursorOffsetXRatio)
    );
    event.dataTransfer.setData(
      'application/x-cursor-offset-y-ratio',
      String(cursorOffsetYRatio)
    );
    event.dataTransfer.setDragImage(
      preview,
      event.clientX - rect.left,
      event.clientY - rect.top
    );
  }
  isDragging.value = true;
}

function onDragEnd() {
  isDragging.value = false;
}

function closeDrawerIfCompact() {
  if (mdAndDown.value) {
    globalStore.drawer = false;
  }
}

function focusPlacement(placeholder: Placeholder | undefined) {
  if (!placeholder?.position) return;
  globalStore.setCurrentPage(placeholder.position.pageNumber);
  scrollToPlaceholder(placeholder, true);
}

async function placeSignature(): Promise<Placeholder | null> {
  // Add a NEW placeholder for this signee on the selected file: create
  // (or reuse the initial empty) slot, then auto-place it so it is offset from
  // the signee's already-placed placeholders, which stay untouched.
  const slot = createOrReuseSignatureSlot(props.participant);
  if (!slot) return null;
  await placePlaceholdersAutomatically(null, [slot.placeholder]);
  await nextTick();

  // Remount the new placeholder's Draggable BEFORE reading its coordinates for
  // persistence. It was first mounted while still invisible (position not yet
  // final, and briefly sharing its template's position object — see
  // `createOrReuseSignatureSlot`), so the draggable library's captured DOM
  // position is stale/wrong until it re-anchors. Without this, the payload
  // built below would read that stale position off the DOM (duplicating
  // another placeholder's coordinates) instead of the actual placed position.
  if (slot.placeholder.uid) {
    await waitForDraggableToSettle();
    requestPlaceholderRemount([slot.placeholder.uid]);
  }
  await nextTick();

  // Persist the signee with ALL its placeholders for the file (the backend
  // replaces a file's positions wholesale, so partial payloads would drop the
  // existing ones). Mirrors the drag-and-drop flow.
  const payload = await buildSigneeUpdatePayload(
    props.participant,
    currentPlaceholders.value as Placeholder[],
    placeholderTemplateRefs?.value,
    createStore.document,
    slot.fileId
  );
  const response = (await createStore.updateInvitee(
    createStore.document!.documentId,
    props.participant.signeeId,
    payload
  )) as AxiosResponse<Signee>;
  if (response?.data) {
    createStore.updateInviteeInLocalData(response.data);
  }

  return slot.placeholder;
}

async function onDesktopAction() {
  // Bound to @click, which the browser only fires when the press-release
  // cycle was a genuine click (no `dragstart` occurred in between). This
  // keeps drag-to-place and click-to-place mutually exclusive without extra
  // state: a drag starting from over this action never reaches here.

  // View: with a single placement there is nothing to step through, so the
  // menu stays disabled and the click jumps straight to that one position
  // (this also covers locked quorum slots, whose shared slot holds exactly
  // one). With 2+ placements the click opens SignaturePositionMenu instead,
  // which owns the navigation. Only Place (unplaced signee) creates a slot.
  if (isPlaced.value) {
    if (!hasMultiplePlacements.value) {
      focusPlacement(navigablePlaceholders.value[0]);
    }
    return;
  }

  // Ignore repeat clicks while the first placement is still persisting, so we
  // don't create a second slot / fire a duplicate request before this flips
  // to the "View" state.
  if (isPlacing.value) return;
  isPlacing.value = true;
  try {
    await placeSignature();
  } finally {
    isPlacing.value = false;
  }
}

async function onCompactPlace() {
  if (isLockedQuorumSlot.value) return;
  // Same re-entry guard as the desktop action: block double/fast taps from
  // creating a second slot before this flips to "View".
  if (isPlacing.value) return;
  isPlacing.value = true;
  let placeholder: Placeholder | null = null;
  try {
    placeholder = await placeSignature();
  } finally {
    isPlacing.value = false;
  }
  // Prefer the live navigable list (scaled coords) once remounted; fall back
  // to the slot we just created if the list has not caught up yet.
  const focused =
    navigablePlaceholders.value.find((p) => p.uid === placeholder?.uid) ??
    placeholder ??
    undefined;
  // Close first so scroll centers in the uncovered viewport.
  closeDrawerIfCompact();
  await nextTick();
  focusPlacement(focused);
}

function onCompactView(event?: MouseEvent) {
  // With 2+ placements let the menu activator open the list (do not steal the
  // click). Navigation + drawer close happen on row pick via @navigated.
  if (hasMultiplePlacements.value) return;
  event?.stopPropagation();
  closeDrawerIfCompact();
  // Defer focus until after the bottom sheet starts closing so the stamp
  // centers in the uncovered viewport.
  nextTick(() => {
    focusPlacement(navigablePlaceholders.value[0]);
  });
}
</script>

<style scoped lang="scss">
// Surface fill only — layout/border/gap/padding/cursor live on Vuetify utilities.
.invitee-card {
  background: rgb(var(--v-theme-surface));
}

// Desktop signee cards keep a fixed 2:1 ratio so the drawer grid scales them
// proportionally (~220×110 → ~184×92 in sequenced groups). No Vuetify utility
// for this; vertical slack is absorbed by the centered flex column.
.invitee-card:not(.invitee-card--approver):not(.invitee-card--compact) {
  aspect-ratio: 2 / 1;
}

// Placed on document: dashed, muted slot.
.invitee-card--placed {
  border-style: dashed;
  background: rgb(var(--v-theme-grey-lighten-4));
}

// Drag-over: highlighted drop target.
.invitee-card--drag-over {
  border-style: dashed;
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
}

// Dragging source: dashed outline; hide content so only the blueprint remains
// (direct children only — never the nested drag ghost).
.invitee-card--dragging {
  border-style: dashed;
  border-color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-surface));

  > .invitee-card__top,
  > .invitee-card__email,
  > .invitee-card__attachment,
  > .invitee-card__compact-actions {
    opacity: 0;
  }
}

// Approver spans the full invitees grid; row layout comes from utilities.
.invitee-card--approver {
  grid-column: 1 / -1;
}

.invitee-card__top {
  min-height: 28px;
}

.invitee-card__sign-icon {
  width: 28px;
  height: 28px;
}

// Hover Place/View overlays the sign icon; hidden until hover/menu-open.
.invitee-card__action {
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.invitee-card__count {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-surface));
  font-size: 9px;
  font-weight: 600;
  line-height: 14px;
  text-align: center;
}

// Compact: keep the badge inside the icon's reserved padding so it doesn't
// collide with the View label on narrow Mobile M cards (e.g. DE "Ansehen").
.invitee-card--compact .invitee-card__count {
  top: -4px;
  right: 0;
  min-width: 12px;
  height: 12px;
  padding: 0 2px;
  border-radius: 6px;
  font-size: 8px;
  line-height: 12px;
}

.invitee-card__email {
  max-width: 100%;
  min-width: 0;
}

// Clamp to 2 lines instead of truncating on the first line; ellipsis only
// appears if the text still overflows after wrapping to a second line.
.invitee-card__email--clamp {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  white-space: normal;
  word-break: break-word;
}

// Strip native button chrome on compact Place/View.
.invitee-card__compact-actions > button,
.invitee-card__compact-actions :deep(button) {
  border: 0;
}

// Off-screen for setDragImage() — park + size match only.
.invitee-card__preview {
  top: 0;
  left: -9999px;
  aspect-ratio: 2 / 1;
  background: rgba(var(--v-theme-primary), 0.1);
}

// Keep View revealed while its menu is open (desktop hover swap).
.invitee-card--menu-open:not(.invitee-card--compact) {
  .invitee-card__sign-icon {
    opacity: 0;
  }

  .invitee-card__action {
    opacity: 1;
    pointer-events: auto;
  }
}

// Desktop hover swap (skipped while dragging / drag-over / compact).
.invitee-card:not(.invitee-card--approver):not(.invitee-card--compact):not(
    .invitee-card--drag-over
  ):not(.invitee-card--dragging):hover {
  background: rgb(var(--v-theme-surface));

  .invitee-card__sign-icon {
    opacity: 0;
  }

  .invitee-card__action {
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
